// src/components/LoaderRequestsQueue.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaMapMarkerAlt, FaLocationArrow, FaCheck, FaTimes, FaRoute, FaBox } from 'react-icons/fa';
import API_URL from '../api/baseURL';

const LoaderRequestsQueue = () => {
    const [activeTab, setActiveTab] = useState('available'); // 'available' or 'active'
    const [availableRequests, setAvailableRequests] = useState([]);
    const [activeJobs, setActiveJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [locationError, setLocationError] = useState('');

    const token = localStorage.getItem('authToken');

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const availableRes = await axios.get(`${API_URL}/loader/available`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAvailableRequests(availableRes.data);

            const activeRes = await axios.get(`${API_URL}/loader/my-jobs`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setActiveJobs(activeRes.data);
        } catch (err) {
            console.error("Error fetching requests:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
        const interval = setInterval(fetchRequests, 15000); // Poll every 15s
        return () => clearInterval(interval);
    }, []);

    // Location Tracking for Active Jobs
    useEffect(() => {
        let watchId;
        if (activeJobs.length > 0) {
            if (navigator.geolocation) {
                watchId = navigator.geolocation.watchPosition(
                    async (position) => {
                        try {
                            const { latitude, longitude } = position.coords;
                            await axios.put(`${API_URL}/loader/location`, {
                                lat: latitude,
                                lng: longitude
                            }, {
                                headers: { Authorization: `Bearer ${token}` }
                            });
                        } catch (err) {
                            console.error("Failed to update location", err);
                        }
                    },
                    (err) => setLocationError("Location access denied or unavailable."),
                    { enableHighAccuracy: true }
                );
            }
        }
        return () => {
            if (watchId) navigator.geolocation.clearWatch(watchId);
        };
    }, [activeJobs.length]);

    const handleAccept = async (id) => {
        try {
            await axios.post(`${API_URL}/loader/requests/${id}/accept`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchRequests();
            setActiveTab('active');
        } catch (err) {
            alert("Failed to accept request");
        }
    };

    const handleDecline = async (id) => {
        try {
            await axios.post(`${API_URL}/loader/requests/${id}/decline`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAvailableRequests(prev => prev.filter(r => r._id !== id));
        } catch (err) {
            alert("Failed to decline request");
        }
    };

    const updateStatus = async (id, status) => {
        try {
            await axios.patch(`${API_URL}/loader/requests/${id}/status`, { status }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            fetchRequests();
        } catch (err) {
            alert("Failed to update status");
        }
    };

    const renderStatusButton = (job) => {
        const nextStatusMap = {
            'accepted': { label: 'Start Pickup', value: 'en_route_pickup' },
            'en_route_pickup': { label: 'Arrived at Pickup', value: 'at_pickup' },
            'at_pickup': { label: 'Start Transport', value: 'in_transit' },
            'in_transit': { label: 'Complete Delivery', value: 'delivered' },
        };

        const next = nextStatusMap[job.status];
        if (!next) return <span className="text-green-600 font-bold uppercase">{job.status.replace('_', ' ')}</span>;

        return (
            <button
                onClick={() => updateStatus(job._id, next.value)}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold shadow hover:bg-blue-700 transition"
            >
                {next.label}
            </button>
        );
    };

    if (loading && availableRequests.length === 0 && activeJobs.length === 0) {
        return <div className="p-8 text-center text-gray-500">Loading requests...</div>;
    }

    return (
        <div className="max-w-3xl mx-auto p-4 space-y-6">
            <div className="flex border-b">
                <button
                    className={`flex-1 py-3 text-center font-semibold ${activeTab === 'available' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                    onClick={() => setActiveTab('available')}
                >
                    Available Requests ({availableRequests.length})
                </button>
                <button
                    className={`flex-1 py-3 text-center font-semibold ${activeTab === 'active' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                    onClick={() => setActiveTab('active')}
                >
                    My Active Jobs ({activeJobs.length})
                </button>
            </div>

            {locationError && (
                <div className="bg-yellow-50 text-yellow-800 p-3 rounded">Warning: {locationError}</div>
            )}

            {activeTab === 'available' && (
                <div className="space-y-4">
                    {availableRequests.length === 0 ? (
                        <div className="text-center py-10 text-gray-500">No new requests in your area.</div>
                    ) : (
                        availableRequests.map(req => (
                            <div key={req._id} className="bg-white border rounded-xl shadow-sm p-5 hover:shadow-md transition">
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-800">{req.distance?.text} Trip</h3>
                                        <p className="text-sm text-gray-500">{req.duration?.text}</p>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xl font-bold text-green-600">
                                            {req.price?.toLocaleString()} FCFA
                                        </div>
                                        <div className="text-xs font-semibold text-emerald-700">
                                            Gain net: {(req.loaderEarnings ?? Math.round(req.price * 0.9))?.toLocaleString()} FCFA
                                            <span className="text-gray-400 font-normal"> (commission 10% déduite)</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-3 mb-6">
                                    <div className="flex items-start gap-3">
                                        <FaLocationArrow className="text-blue-500 mt-1" />
                                        <div>
                                            <p className="text-xs text-gray-400 uppercase">Pickup</p>
                                            <p className="text-gray-800 font-medium">{req.pickup.address}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <FaMapMarkerAlt className="text-red-500 mt-1" />
                                        <div>
                                            <p className="text-xs text-gray-400 uppercase">Dropoff</p>
                                            <p className="text-gray-800 font-medium">{req.dropoff.address}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        onClick={() => handleDecline(req._id)}
                                        className="flex justify-center items-center gap-2 border border-gray-300 text-gray-700 py-2 rounded-lg font-semibold hover:bg-gray-50"
                                    >
                                        <FaTimes /> Decline
                                    </button>
                                    <button
                                        onClick={() => handleAccept(req._id)}
                                        className="flex justify-center items-center gap-2 bg-black text-white py-2 rounded-lg font-semibold hover:bg-gray-800"
                                    >
                                        <FaCheck /> Accept
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {activeTab === 'active' && (
                <div className="space-y-4">
                    {activeJobs.length === 0 ? (
                        <div className="text-center py-10 text-gray-500">You have no active jobs. Switch to "Available" to find work.</div>
                    ) : (
                        activeJobs.map(job => (
                            <div key={job._id} className="bg-white border-2 border-blue-500 rounded-xl shadow-md p-5">
                                <div className="flex justify-between items-center mb-4 border-b pb-3">
                                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide">
                                        {job.status.replace('_', ' ')}
                                    </span>
                                    <div className="text-right text-sm">
                                        <span className="font-bold text-gray-800">{job.price?.toLocaleString()} FCFA</span>
                                        <span className="block text-xs font-semibold text-emerald-700">
                                            Net: {(job.loaderEarnings ?? Math.round(job.price * 0.9))?.toLocaleString()} FCFA
                                        </span>
                                    </div>
                                    <a
                                        href={`https://www.google.com/maps/dir/?api=1&origin=${job.pickup.lat},${job.pickup.lng}&destination=${job.dropoff.lat},${job.dropoff.lng}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-blue-600 font-semibold text-sm flex items-center gap-1 hover:underline"
                                    >
                                        <FaRoute /> Open GPS
                                    </a>
                                </div>

                                <div className="space-y-4 mb-6">
                                    <div className="flex items-center gap-3 bg-gray-50 p-3 rounded">
                                        <div className="bg-white p-2 rounded shadow-sm"><FaBox className="text-gray-600" /></div>
                                        <div>
                                            <p className="text-xs text-gray-400 uppercase">Payload</p>
                                            <p className="font-semibold text-gray-800">{job.car?.brand} {job.car?.model} ({job.car?.plateNumber})</p>
                                        </div>
                                    </div>

                                    {/* Add more details if needed */}
                                </div>

                                {renderStatusButton(job)}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
};

export default LoaderRequestsQueue;
